"use client";

import { useWebsiteHomeHref } from "@/hooks/use-website-home-href";

import { useState, useRef, useCallback, useEffect, Suspense } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { signIn } from "@/lib/auth/auth";
import { useAuthStore } from "@/stores/auth-store";
import { ArrowLeft, Mail, Lock, WifiOff, Eye, EyeOff } from "lucide-react";
import { EmailCodeStep, type EmailStepSession } from "@/components/auth/email-code-step";
import { getRedirectFromParamsOrStorage, clearRedirectUrl } from "@/lib/utils/redirect-storage";
import { useSearchParamValue } from "@/hooks/use-search-params-safe";
import { useNetworkStatus } from "@/hooks/use-network-status";
import { useTurnstileToken } from "@/hooks/use-turnstile-token";
import { trackEvent, identifyUser } from "@/lib/posthog-helpers";
import {
  alignServerAuthCookiesBeforeAppEntry,
  clearClientAuthIfServerSaysSignedOut,
  logAuthDecision,
} from "@/lib/auth/verify-web-cookie-session";
import { useTranslations } from "@/hooks/use-translations";
import { loadPlugin } from "@/lib/capacitor/load-plugin";

type Status = "idle" | "loading" | "error";

function LoginPageContent() {
  const { t } = useTranslations();
  const router = useRouter();
  const login = useAuthStore((state) => state.login);
  const { user, isAuthenticated, syncSession, resolveMembershipOnLogin } = useAuthStore();
  const redirectToParam = useSearchParamValue("redirectTo");
  const messageParam = useSearchParamValue("message");
  const homeHref = useWebsiteHomeHref();
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const { isOnline } = useNetworkStatus();
  
  // Track page load time
  const pageLoadTimeRef = useRef<number>(Date.now());
  const submitStartTimeRef = useRef<number | null>(null);
  /* The status message and the security check both sit BELOW the Sign in button, so on a
     phone they land off the bottom of the screen. Pressing Sign in then looks like it did
     nothing at all, which is exactly how it was reported: "pressing sign in just got stuck".
     Scroll whatever the person now needs to see back into view. */
  const messageRef = useRef<HTMLDivElement | null>(null);
  const turnstileAreaRef = useRef<HTMLDivElement | null>(null);
  const markPerf = useCallback((label: string) => {
    if (typeof window === "undefined" || typeof performance === "undefined") return;
    const markName = `login:${label}`;
    performance.mark(markName);
    console.log(`[Login Perf] ${markName}`, { timestamp: new Date().toISOString() });
  }, []);
  
  // Log page load
  useEffect(() => {
    console.log("[Login] 🚀 Page loaded", {
      timestamp: new Date().toISOString(),
      pathname: typeof window !== "undefined" ? window.location.pathname : "unknown",
    });
  }, []);
  
  // CRITICAL: Check for existing session BEFORE rendering login form
  // This ensures users with valid sessions are automatically signed in
  // without seeing the login screen (like Facebook/Instagram)
  // CRITICAL: Always resolve within 8s - prevents indefinite "Checking session..." on iPhone
  useEffect(() => {
    const SESSION_CHECK_TIMEOUT_MS = 8000;
    const SYNC_SESSION_TIMEOUT_MS = 4000;
    const forceResolveTimer = setTimeout(() => {
      setIsCheckingSession(false);
    }, SESSION_CHECK_TIMEOUT_MS);

    const checkExistingSession = async () => {
      try {
        if (typeof window !== "undefined") {
          const { sessionRestorePromise, hasStoredRefreshToken } = await import("@/lib/supabase/client");
          const isCapacitor = /Capacitor/i.test(navigator.userAgent);
          // Fast path: no token on web - skip wait; sessionRestorePromise resolves quickly
          if (!isCapacitor && !hasStoredRefreshToken()) {
            await Promise.race([
              syncSession(),
              new Promise<void>((_, reject) =>
                setTimeout(() => reject(new Error("Sync timeout")), SYNC_SESSION_TIMEOUT_MS)
              ),
            ]).catch(() => {});
          } else {
            await Promise.race([
              sessionRestorePromise,
              new Promise((resolve) => setTimeout(resolve, 6000)),
            ]).catch(() => {});
            await new Promise((resolve) => setTimeout(resolve, 100));
            await Promise.race([
              syncSession(),
              new Promise<void>((_, reject) =>
                setTimeout(() => reject(new Error("Sync timeout")), SYNC_SESSION_TIMEOUT_MS)
              ),
            ]).catch(() => {});
          }
        } else {
          await syncSession();
        }
        
        const state = useAuthStore.getState();
        
        // If user is authenticated, ensure SSR cookies match middleware before leaving auth (native + web).
        if (state.isAuthenticated && state.user) {
          const cookieResult = await alignServerAuthCookiesBeforeAppEntry();
          if (cookieResult !== "ok") {
            logAuthDecision("Login session check: cookie-session not OK, not redirecting to app", {
              cookieResult,
            });
            await clearClientAuthIfServerSaysSignedOut(cookieResult, "login_existing_session_cookie_mismatch");
            return;
          }
          // Get redirect URL from params or storage, default to dashboard
          const redirectUrl = getRedirectFromParamsOrStorage(
            typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null,
            "/dashboard"
          );
          clearRedirectUrl();
          router.replace(redirectUrl); // Use replace to avoid adding to history
          return;
        }
      } catch (error) {
        console.error("[Login] Error checking session:", error);
      } finally {
        clearTimeout(forceResolveTimer);
        // Only show login form if no valid session found
        setIsCheckingSession(false);
      }
    };
    
    checkExistingSession();
    return () => clearTimeout(forceResolveTimer);
  }, [router, syncSession]);
  
  // Email + password is the only way in. "Sign in with your phone" sent a text message code and
  // was retired on 2026-10-08: texts cost money on Twilio, it had been used once in the previous
  // 90 days, and every member has an email address and a password. On a device that has not
  // signed in before, the second step is now a 6-digit code sent to the account's email.
  const loginMethod = "email" as const;

  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  // Set when the password was right but this device has to pass the emailed code first.
  const [stepUp, setStepUp] = useState<{
    challengeToken: string;
    emailHint: string;
    codeSent: boolean;
    resendInSeconds: number;
  } | null>(null);
  
  // Status and messages
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState<string | null>(null);

  // Show success message from URL (e.g. after password reset redirect from /auth/set-password)
  useEffect(() => {
    if (messageParam) {
      setMessage(messageParam);
    }
  }, [messageParam]);

  // Turnstile: widget provides token; we pass it to API → server verifies with Cloudflare only (we never pass token to Supabase). Phone OTP tab uses Turnstile for send-sms-otp.
  const {
    token: turnstileToken,
    tokenTimestamp: turnstileTokenTimestamp,
    getValidToken,
    getTokenForSubmit,
    TurnstileComponent,
    isVerifying: isTurnstileVerifying,
    isVerified: isTurnstileVerified,
    isTurnstileEnabled,
    isAwaitingInteraction,
    resetWidget,
  } = useTurnstileToken({
    visible: true,
  });

  // The security check is a third-party script, so it can fail for reasons that have nothing to do
  // with the person trying to sign in: a Cloudflare misconfiguration, a blocked domain, a captive
  // wifi portal. Until now the sign-in button simply sat on "Completing verification…" with a
  // spinner for as long as that lasted, which is forever. No message, no retry, no way forward: the
  // app was unusable and gave no clue why. That is exactly what happened on 2026-07-29 when
  // pre-clearance was enabled on a domain that is not proxied through Cloudflare.
  //
  // So bound the wait. If the check has not completed in STALL_MS, stop pretending it is still
  // working, say so in plain words, and offer a retry that re-mounts the widget.
  //
  // But the wait must only be bounded when nothing is happening. Cloudflare runs this widget in
  // "managed" mode, which means it decides per visitor whether to pass them silently or to put up a
  // "Verify you are human" checkbox. When it asks, there is no token until the person clicks, and
  // that can obviously take longer than twelve seconds. Counting that time as a stall told people
  // their connection had failed while the very checkbox they needed sat on screen, and the retry
  // then reset the widget out from under them, which started the whole thing again. Only treat the
  // check as stalled when it is genuinely unattended.
  // Unattended, nothing should take twelve seconds. Waiting on a person is different: they have to
  // notice the checkbox and click it, so give that a generous bound. It still needs one, because an
  // interactive challenge can itself fail to load and would otherwise wait forever.
  const TURNSTILE_STALL_MS = 12000;
  const TURNSTILE_INTERACTIVE_STALL_MS = 90000;
  const [turnstileStalled, setTurnstileStalled] = useState(false);
  const turnstilePending = isTurnstileEnabled && !isTurnstileVerified;

  useEffect(() => {
    if (!turnstilePending) {
      setTurnstileStalled(false);
      return;
    }
    setTurnstileStalled(false);
    const timer = setTimeout(
      () => setTurnstileStalled(true),
      isAwaitingInteraction ? TURNSTILE_INTERACTIVE_STALL_MS : TURNSTILE_STALL_MS,
    );
    return () => clearTimeout(timer);
  }, [turnstilePending, isAwaitingInteraction]);

  /* Bring the thing the person now has to look at back onto the screen.
     Both the status message and the security check render after the form, so on a phone they
     are below the fold. Without this, a blocked sign-in changes nothing in the visible area
     and reads as a frozen screen. Respects reduced-motion, and never fights an active scroll. */
  useEffect(() => {
    const target =
      isAwaitingInteraction && !turnstileStalled
        ? turnstileAreaRef.current
        : status === "error" && message
        ? messageRef.current
        : null;
    if (!target) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const id = window.setTimeout(() => {
      target.scrollIntoView({ block: "center", behavior: reduce ? "auto" : "smooth" });
    }, 60);
    return () => window.clearTimeout(id);
  }, [status, message, isAwaitingInteraction, turnstileStalled]);

  function retryTurnstile() {
    setTurnstileStalled(false);
    try {
      resetWidget();
    } catch {
      // If the widget cannot be re-mounted, a reload is the only remaining route.
      window.location.reload();
    }
  }

  // CRITICAL: Add app lifecycle listeners for Android debugging
  // NOTE: This must be after state declarations to use isTurnstileVerifying and status
  useEffect(() => {
    const isAndroidCapacitor = typeof window !== "undefined" && /Android/i.test(navigator.userAgent) && /Capacitor/i.test(navigator.userAgent);
    
    if (!isAndroidCapacitor) return;
    
    // CRITICAL: Add global error handlers to prevent app from closing on unhandled errors
    const handleError = (event: ErrorEvent) => {
      console.error("[Login] [Android Debug] Global error caught:", {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error,
        timestamp: new Date().toISOString(),
      });
      // Prevent default error handling that might close the app
      event.preventDefault();
      return false;
    };
    
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error("[Login] [Android Debug] Unhandled promise rejection:", {
        reason: event.reason,
        promise: event.promise,
        timestamp: new Date().toISOString(),
      });
      // Prevent default error handling that might close the app
      event.preventDefault();
    };
    
    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    
    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    };
  }, []);
  
  // Separate useEffect for app lifecycle listeners
  useEffect(() => {
    const isAndroidCapacitor = typeof window !== "undefined" && /Android/i.test(navigator.userAgent) && /Capacitor/i.test(navigator.userAgent);
    
    if (!isAndroidCapacitor) return;
    
    console.log("[Login] [Android Debug] Setting up app lifecycle listeners...");
    
    // Listen for app state changes (foreground/background)
    const setupAppListeners = async () => {
      try {
        const mod = await loadPlugin("App", () => import("@capacitor/app"));
        if (!mod?.App) return; // older build with no App plugin
        const { App } = mod;
        
        // Listen for app state changes
        const listener = await App.addListener("appStateChange", (state) => {
          console.log("[Login] [Android Debug] App state changed:", {
            isActive: state.isActive,
            timestamp: new Date().toISOString(),
            currentUrl: typeof window !== "undefined" ? window.location.href : "unknown",
          });
        });
        
        // Listen for app URL open (deep links)
        const urlListener = await App.addListener("appUrlOpen", (data) => {
          const isVerifying = isTurnstileVerifying || status === "loading";
          
          console.log("[Login] [Android Debug] App URL opened:", {
            url: data.url,
            timestamp: new Date().toISOString(),
            isVerifying,
            status,
          });
          
          // CRITICAL: Block external navigation during Cloudflare verification
          if (isVerifying && data.url && !data.url.includes(window.location.origin)) {
            console.warn("[Login] [Android Debug] Blocked external URL during verification:", data.url);
            return;
          }
        });
        
        // Listen for back button
        const backButtonListener = await App.addListener("backButton", () => {
          const isVerifying = isTurnstileVerifying || status === "loading";
          
          console.log("[Login] [Android Debug] Back button pressed:", {
            timestamp: new Date().toISOString(),
            currentUrl: typeof window !== "undefined" ? window.location.href : "unknown",
            isVerifying,
            status,
          });
          
          // CRITICAL: Prevent back button during verification to avoid interrupting Cloudflare
          if (isVerifying) {
            console.log("[Login] [Android Debug] Back button blocked during verification");
            return;
          }
        });
        
        console.log("[Login] [Android Debug] App lifecycle listeners registered");
        
        return () => {
          listener.remove();
          urlListener.remove();
          backButtonListener.remove();
        };
      } catch (error) {
        console.error("[Login] [Android Debug] Error setting up app listeners:", error);
      }
    };
    
    setupAppListeners();
    
    // Listen for page visibility changes (WebView might be hidden)
    const handleVisibilityChange = () => {
      console.log("[Login] [Android Debug] Page visibility changed:", {
        hidden: document.hidden,
        visibilityState: document.visibilityState,
        timestamp: new Date().toISOString(),
        currentUrl: typeof window !== "undefined" ? window.location.href : "unknown",
      });
    };
    
    document.addEventListener("visibilitychange", handleVisibilityChange);
    
    // Listen for beforeunload (app might be closing)
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const isVerifying = isTurnstileVerifying || status === "loading";
      
      console.log("[Login] [Android Debug] Before unload event:", {
        timestamp: new Date().toISOString(),
        currentUrl: typeof window !== "undefined" ? window.location.href : "unknown",
        isVerifying,
        status,
      });
      
      // CRITICAL: Try to prevent unload during Cloudflare verification
      if (isVerifying) {
        console.warn("[Login] [Android Debug] Attempting to prevent unload during verification");
        e.preventDefault();
        e.returnValue = ''; // Chrome requires returnValue
        return '';
      }
    };
    
    window.addEventListener("beforeunload", handleBeforeUnload);
    
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isTurnstileVerifying, status]);
  
  // Enhanced email validation
  const isValidEmail = (email: string): boolean => {
    if (!email) return false;
    try {
      // Use synchronous validation - the function is synchronous
      const { validateEmail } = require("@/lib/validation/email");
      const result = validateEmail(email);
      return result.isValid;
    } catch {
      // Fallback to basic validation if module fails to load
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }
  };

  const [emailValidationError, setEmailValidationError] = useState<string | null>(null);

  // Validate email and set error message
  const validateEmailInput = (emailValue: string) => {
    if (!emailValue) {
      setEmailValidationError(null);
      return false;
    }
    try {
      const { validateEmail } = require("@/lib/validation/email");
      const result = validateEmail(emailValue);
      if (!result.isValid) {
        setEmailValidationError(result.error || "Invalid email address");
        return false;
      }
      setEmailValidationError(null);
      return true;
    } catch {
      // Fallback validation
      const basicValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue);
      if (!basicValid) {
        setEmailValidationError("Invalid email format");
        return false;
      }
      setEmailValidationError(null);
      return true;
    }
  };

  const isFormValid = (): boolean => {
    if (!email || !isValidEmail(email)) return false;
    return !!password;
  };

  // Handle successful authentication
  // PERFORMANCE: Navigate immediately after auth success - don't block on profile/subscription/verification
  const handleSuccessfulAuth = async (userId: string, userEmail: string) => {
    const authSuccessTime = Date.now();
    const clickToAuthTime = submitStartTimeRef.current 
      ? authSuccessTime - submitStartTimeRef.current 
      : null;
    
    // Performance logging: Click → Auth Success
    console.log("[Login] ⚡ PERFORMANCE: Auth success", {
      clickToAuthTime: clickToAuthTime ? `${clickToAuthTime}ms` : "unknown",
      timestamp: new Date().toISOString(),
    });
    
    const currentTier = useAuthStore.getState().membershipTier;
    const initialTier =
      currentTier === "premium" || currentTier === "elite" ? currentTier : "free";

    // Set minimal auth state immediately (synchronous - no await)
    login({
      id: userId,
      email: userEmail,
      profileId: userId,
      membershipTier: initialTier,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });

    // Analytics: identify user and record login event.
    // PostHogProvider will also sync on auth state change, but we fire
    // explicitly here so the method is captured accurately.
    identifyUser(userId, { email: userEmail });
    trackEvent("user_logged_in", { method: loginMethod });

    // Resolve authoritative membership before/around first post-login paint.
    await Promise.race([
      resolveMembershipOnLogin({ forceSync: true }),
      new Promise<void>((resolve) => setTimeout(resolve, 1200)),
    ]).catch(() => {});

    setStatus("idle");
    setMessage("Signed in. Loading...");

    // Get redirect URL from params or storage, default to dashboard
    const redirectUrl = getRedirectFromParamsOrStorage(
      typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null,
      "/dashboard"
    );
    
    // Clear redirect storage after reading
    clearRedirectUrl();

    // PERFORMANCE: Fire profile pre-fetch and dashboard-critical data in parallel - do NOT block navigation
    // Dashboard boot will use profile if ready; critical data (notifications, matches, events, etc.) will be loading or ready when user lands.
    const profileFetchStart = Date.now();
    import("@/stores/profile-store").then(({ useProfileStore }) => {
      const { fetchCurrentProfile } = useProfileStore.getState();
      Promise.race([
        fetchCurrentProfile(userId, false),
        new Promise<void>((_, reject) =>
          setTimeout(() => reject(new Error("Profile fetch timeout")), 4000)
        ),
      ]).catch((err) => {
        console.warn("[Login] Profile pre-fetch skipped (non-fatal):", err?.message || err);
      }).finally(() => {
        console.log("[Login] ⚡ Profile pre-fetch", {
          ms: Date.now() - profileFetchStart,
          timestamp: new Date().toISOString(),
        });
      });
    }).catch((e) => {
      console.warn("[Login] Profile pre-fetch error (non-fatal):", e);
    });

    // Preload dashboard-critical data so Insights, Matches, Notifications, etc. are ready when user lands
    import("@/lib/preload/dashboard-critical").then(({ preloadDashboardCriticalData }) => {
      preloadDashboardCriticalData(userId);
    }).catch(() => {});
    
    const navigationStartTime = Date.now();
    markPerf("navigation_started");
    
    // CRITICAL: Log before navigation to debug redirect issues
    console.log("[Login] 🚀 About to redirect", {
      redirectUrl,
      currentPath: typeof window !== "undefined" ? window.location.pathname : "unknown",
      isAuthenticated: useAuthStore.getState().isAuthenticated,
      hasUser: !!useAuthStore.getState().user,
      timestamp: new Date().toISOString(),
    });
    
    // Navigate immediately (non-blocking)
    router.replace(redirectUrl);
    
    // CRITICAL: Also use window.location as fallback if router.replace doesn't work
    // This ensures redirect happens even if Next.js router has issues
    setTimeout(() => {
      if (typeof window !== "undefined" && window.location.pathname === "/auth/login") {
        console.warn("[Login] ⚠️ Still on login page after redirect, forcing navigation");
        window.location.href = redirectUrl;
      }
    }, 50);
    
    const navigationTime = Date.now() - navigationStartTime;
    const authToNavigationTime = navigationTime;
    
    // Performance logging: Auth Success → Route Change
    console.log("[Login] ⚡ PERFORMANCE: Navigation initiated", {
      authToNavigationTime: `${authToNavigationTime}ms`,
      totalTimeFromClick: clickToAuthTime ? `${clickToAuthTime + authToNavigationTime}ms` : "unknown",
      redirectUrl,
      timestamp: new Date().toISOString(),
    });
    if (typeof window !== "undefined" && typeof performance !== "undefined") {
      try {
        performance.measure("login:submit_to_signin_api", "login:submit_started", "login:signin_api_completed");
        performance.measure("login:submit_to_set_session", "login:submit_started", "login:set_session_completed");
        performance.measure("login:submit_to_navigation", "login:submit_started", "login:navigation_started");
      } catch {
        // Some flows (e.g., phone OTP) may not set all marks; ignore safely.
      }
    }
    
    // PERFORMANCE: Start data fetching in parallel AFTER navigation (non-blocking)
    // These will happen in the background while user sees the dashboard skeleton
    Promise.all([
      // Fetch profile (non-blocking)
      (async () => {
        const profileStartTime = Date.now();
        try {
          const { useProfileStore } = await import("@/stores/profile-store");
          const { fetchCurrentProfile } = useProfileStore.getState();
          await fetchCurrentProfile(userId, true).catch((error) => {
            console.warn("[Login] Profile fetch failed (non-fatal):", error);
          });
          const profileTime = Date.now() - profileStartTime;
          console.log("[Login] ⚡ PERFORMANCE: Profile fetched", {
            profileTime: `${profileTime}ms`,
            timestamp: new Date().toISOString(),
          });
        } catch (error) {
          console.warn("[Login] Profile fetch error (non-fatal):", error);
        }
      })(),
      
      // Fetch entitlements/subscription (non-blocking)
      (async () => {
        const entitlementsStartTime = Date.now();
        try {
          const { authFetch } = await import("@/lib/api/auth-fetch");
          await authFetch("/api/me/entitlements", {
            credentials: "include",
          }).catch((error) => {
            console.warn("[Login] Entitlements fetch failed (non-fatal):", error);
          });
          const entitlementsTime = Date.now() - entitlementsStartTime;
          console.log("[Login] ⚡ PERFORMANCE: Entitlements fetched", {
            entitlementsTime: `${entitlementsTime}ms`,
            timestamp: new Date().toISOString(),
          });
        } catch (error) {
          console.warn("[Login] Entitlements fetch error (non-fatal):", error);
        }
      })(),
      
      // Fetch feature flags (non-blocking)
      (async () => {
        const flagsStartTime = Date.now();
        try {
          const { getFeatureFlags } = await import("@/lib/feature-flags/client");
          await getFeatureFlags(true).catch((error) => {
            console.warn("[Login] Feature flags fetch failed (non-fatal):", error);
          });
          const flagsTime = Date.now() - flagsStartTime;
          console.log("[Login] ⚡ PERFORMANCE: Feature flags fetched", {
            flagsTime: `${flagsTime}ms`,
            timestamp: new Date().toISOString(),
          });
        } catch (error) {
          console.warn("[Login] Feature flags fetch error (non-fatal):", error);
        }
      })(),
      
      // Sync session (non-blocking)
      (async () => {
        const syncStartTime = Date.now();
        try {
          await syncSession().catch((error) => {
            console.warn("[Login] Session sync failed (non-fatal):", error);
          });
          const syncTime = Date.now() - syncStartTime;
          console.log("[Login] ⚡ PERFORMANCE: Session synced", {
            syncTime: `${syncTime}ms`,
            timestamp: new Date().toISOString(),
          });
        } catch (error) {
          console.warn("[Login] Session sync error (non-fatal):", error);
        }
      })(),
    ]).then(() => {
      const totalPostLoginTime = Date.now() - navigationStartTime;
      console.log("[Login] ⚡ PERFORMANCE: All post-login fetches complete", {
        totalPostLoginTime: `${totalPostLoginTime}ms`,
        timestamp: new Date().toISOString(),
      });
    }).catch((error) => {
      console.warn("[Login] Some post-login fetches failed (non-fatal):", error);
    });
  };

  /**
   * Hand a session from the server to the Supabase client and go into the app. Used both when
   * the password alone was enough (a device that has signed in before) and after the emailed
   * code. Returns false after showing an error, true once navigation has started.
   */
  const enterWithSession = async (
    sessionToSet: EmailStepSession,
    user: { id: string; email?: string | null }
  ): Promise<boolean> => {

      console.log("[EMAIL LOGIN] 🔐 Step 4: Setting session", {
        hasAccessToken: !!sessionToSet.access_token,
        hasRefreshToken: !!sessionToSet.refresh_token,
        timestamp: new Date().toISOString(),
      });

      const { supabase, clearAuthBackoff, withSetSessionGuard, isSetSessionInProgress } = await import(
        "@/lib/supabase/client"
      );
      const session = sessionToSet;

      clearAuthBackoff();

      console.log("[EMAIL LOGIN] before setSession", {
        guardActive: isSetSessionInProgress(),
        path: typeof window !== "undefined" ? window.location.pathname : null,
        timestamp: new Date().toISOString(),
      });

      // CRITICAL: withSetSessionGuard prevents our fetch interceptor from clearing storage on
      // 400/429 during setSession - which would abort the operation and trigger SIGNED_OUT storm.
      const setSessionResult = await withSetSessionGuard(async () => {
        const maxAttempts = 3;
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
          try {
            console.log("[EMAIL LOGIN] setSession attempt", {
              attempt,
              guardActiveInside: isSetSessionInProgress(),
              timestamp: new Date().toISOString(),
            });
            const result = await supabase.auth.setSession({
              access_token: session.access_token,
              refresh_token: session.refresh_token,
            });
            console.log("[EMAIL LOGIN] after setSession success", {
              attempt,
              hasError: !!result.error,
              hasSession: !!result.data?.session,
              timestamp: new Date().toISOString(),
            });
            return result;
          } catch (err) {
            const isAbortError =
              err instanceof Error &&
              (err.name === "AbortError" || err.message?.toLowerCase().includes("aborted"));
            console.warn("[EMAIL LOGIN] setSession threw", {
              attempt,
              isAbortError,
              name: err instanceof Error ? err.name : null,
              message: err instanceof Error ? err.message : String(err),
              guardActive: isSetSessionInProgress(),
              hint: isAbortError
                ? "Often caused by supabase.auth.signOut() elsewhere (e.g. cookie-desync clear). Skipped while guard is active."
                : null,
              timestamp: new Date().toISOString(),
            });
            if (isAbortError && attempt < maxAttempts) {
              console.warn(`[EMAIL LOGIN] setSession aborted (attempt ${attempt}/${maxAttempts}), retrying...`);
              await new Promise((r) => setTimeout(r, 300 * attempt));
            } else {
              throw err;
            }
          }
        }
        return null as Awaited<ReturnType<typeof supabase.auth.setSession>> | null;
      });
      if (!setSessionResult) {
        throw new Error("Failed to set session after retries");
      }
      markPerf("set_session_completed");

      // Extract error properties before early return (for TypeScript type narrowing)
      const sessionError = setSessionResult.error;
      const errorMessage = sessionError?.message;
      const errorCode = sessionError?.status;

      if (sessionError) {
        console.error("[EMAIL LOGIN] ❌ Failed to set session:", sessionError);
        setStatus("error");
        setMessage("Failed to create session. Please try again.");
        return false;
      }

      // CRITICAL: Verify session was set correctly before redirecting
      if (!setSessionResult.data?.session) {
        console.error("[EMAIL LOGIN] ❌ Session not set after setSession call", {
          hasError: !!sessionError,
          errorMessage: errorMessage,
          errorCode: errorCode,
          hasData: !!setSessionResult.data,
          hasUser: !!setSessionResult.data?.user,
          dataKeys: setSessionResult.data ? Object.keys(setSessionResult.data) : [],
        });
        setStatus("error");
        setMessage("Failed to create session. Please try again.");
        return false;
      }
      
      // Additional verification: check that session has required tokens
      if (!setSessionResult.data.session.access_token || !setSessionResult.data.session.refresh_token) {
        console.error("[EMAIL LOGIN] ❌ Session missing required tokens", {
          hasAccessToken: !!setSessionResult.data.session.access_token,
          hasRefreshToken: !!setSessionResult.data.session.refresh_token,
        });
        setStatus("error");
        setMessage("Failed to create session. Please try again.");
        return false;
      }

      console.log("[EMAIL LOGIN] ✅ Step 5: Login successful, redirecting immediately", {
        userId: user.id,
        hasSession: !!setSessionResult.data.session,
        timestamp: new Date().toISOString(),
      });
      
      // PERFORMANCE: Navigate immediately - don't block on syncSession
      // Session is already set in Supabase (line 780-821)
      // handleSuccessfulAuth will set minimal auth state and navigate immediately
      // Background sync will happen after navigation (non-blocking)
      // Success - redirect immediately
      handleSuccessfulAuth(user.id, user.email || email);
      return true;
  };

  // Email + Password flow: submit only AFTER we have a valid Turnstile token (no refresh/remount during submit).
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isFormValid()) {
      setStatus("error");
      setMessage("Please enter a valid email and password.");
      return;
    }

    submitStartTimeRef.current = Date.now();
    
    console.log("[EMAIL LOGIN] 📤 Submit started", {
      timestamp: new Date().toISOString(),
    });
    console.log("[EMAIL LOGIN] Turnstile enabled for email+password", {
      isTurnstileEnabled,
      timestamp: new Date().toISOString(),
    });

    // Get token for submit when Turnstile is enabled (no refresh/remount). When Turnstile not configured, token is optional.
    const submitToken = isTurnstileEnabled ? await getTokenForSubmit() : null;
    const tokenAgeSeconds = turnstileTokenTimestamp != null
      ? Math.round((Date.now() - turnstileTokenTimestamp) / 1000)
      : null;

    console.log("[EMAIL LOGIN] Token at submit time", {
      hasToken: !!submitToken,
      tokenLength: submitToken?.length ?? 0,
      tokenAgeSeconds,
      isTurnstileEnabled,
      refreshedAtSubmit: false,
      timestamp: new Date().toISOString(),
    });

    if (isTurnstileEnabled && (!submitToken || submitToken.length < 10)) {
      console.warn("[EMAIL LOGIN] No valid Turnstile token at submit, blocking sign-in", {
        hasToken: !!submitToken,
        tokenLength: submitToken?.length ?? 0,
        timestamp: new Date().toISOString(),
      });
      setStatus("error");
      setMessage("One more step: please complete the security check below, then press Sign in again.");
      return;
    }
    setStatus("loading");
    setMessage(null);

    try {
      const apiStartTime = Date.now();
      markPerf("signin_api_started");
      console.log("[EMAIL LOGIN] 🌐 Calling sign-in API", {
        timestamp: new Date().toISOString(),
        hasCaptchaToken: !!submitToken,
        tokenLength: submitToken?.length ?? 0,
      });
      
      let response;
      try {
        response = await signIn(email, password, submitToken ?? undefined);
      } catch (signInError) {
        console.error("[EMAIL LOGIN] ❌ Exception calling signIn function", {
          error: signInError instanceof Error ? signInError.message : String(signInError),
          errorStack: signInError instanceof Error ? signInError.stack : undefined,
          errorType: typeof signInError,
          errorStringified: JSON.stringify(signInError, Object.getOwnPropertyNames(signInError)),
          timestamp: new Date().toISOString(),
        });
        setStatus("error");
        const errorMsg = signInError instanceof Error 
          ? signInError.message 
          : typeof signInError === "string" 
            ? signInError 
            : "Sign in failed. Please try again.";
        setMessage(errorMsg || "Sign in failed. Please try again.");
        return;
      }
      
      // Validate response structure
      if (!response || typeof response !== "object") {
        console.error("[EMAIL LOGIN] ❌ Invalid response structure", {
          responseType: typeof response,
          response: response,
          timestamp: new Date().toISOString(),
        });
        setStatus("error");
        setMessage("Sign in failed. Please try again.");
        return;
      }
      
      // 5) Instrumentation: exact API/Supabase error code and message
      console.log("[EMAIL LOGIN] 📥 API response received", {
        success: response.success,
        hasUser: !!response.user,
        errorMessage: response.message,
        errorCode: response.errorCode,
        apiTime: `${Date.now() - apiStartTime}ms`,
        timestamp: new Date().toISOString(),
      });
      markPerf("signin_api_completed");

      // Right password, new device: move to the emailed code. No session exists yet.
      if (response.requiresStepUp && response.challengeToken) {
        setStepUp({
          challengeToken: response.challengeToken,
          emailHint: response.emailHint || email,
          codeSent: response.codeSent !== false,
          resendInSeconds: response.resendInSeconds ?? 60,
        });
        setPassword("");
        setStatus("idle");
        setMessage(null);
        resetWidget();
        return;
      }

      if (!response.success || !response.user) {
        const errorDetails = {
          success: response?.success ?? false,
          hasUser: !!response?.user,
          errorMessage: response?.message ?? "Unknown error",
          errorCode: response?.errorCode,
          responseKeys: response ? Object.keys(response) : [],
        };
        console.error("[EMAIL LOGIN] ❌ Login failed", errorDetails);

        setStatus("error");
        // Show "Human verification failed" only when API indicates captcha/verification failure
        const isCaptchaError =
          response.errorCode === "captcha_failed" ||
          response.errorCode === "invalid_input_response" ||
          (response.message || "").toLowerCase().includes("human verification") ||
          (response.message || "").toLowerCase().includes("verification failed") ||
          (response.message || "").toLowerCase().includes("captcha");

        if (isCaptchaError) {
          setMessage("Human verification failed. Please complete the verification above and try again.");
        } else {
          // Show user-friendly message only - never expose technical details (e.g. "Supabase: ...")
          setMessage(response.message || "Invalid email or password.");
        }
        resetWidget();
        return;
      }

      // STEP 4: Set session and redirect
      if (!response.session) {
        console.error("[EMAIL LOGIN] ❌ No session in response");
        setStatus("error");
        setMessage("Failed to create session. Please try again.");
        return;
      }
      await enterWithSession(response.session, response.user);
    } catch (error) {
      console.error("[EMAIL LOGIN] ❌ Error signing in:", {
        error,
        errorType: typeof error,
        errorStringified: JSON.stringify(error, Object.getOwnPropertyNames(error)),
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined,
        timestamp: new Date().toISOString(),
      });
      setStatus("error");
      // Reset widget on error so user can get a fresh token (except AbortError - often transient)
      const isAbortError =
        error instanceof Error &&
        (error.name === "AbortError" || error.message?.toLowerCase().includes("aborted"));
      if (!isAbortError) {
        resetWidget();
      }

      // Provide helpful error message
      if (error instanceof Error) {
        if (isAbortError) {
          setMessage("Session setup was interrupted. Please try again.");
        } else if (error.message.includes("network") || error.message.includes("fetch")) {
          setMessage("Network error. Please check your connection and try again.");
        } else {
          setMessage(error.message || "Sign in failed. Please try again.");
        }
      } else {
        setMessage("Sign in failed. Please try again.");
      }
    }
  };

  // Show loading screen while checking for existing session
  if (isCheckingSession) {
    return (
      <div className="flex flex-1 min-h-0 flex-col md:block">
        <div className="space-y-3 pb-0 flex-1 min-h-0 overflow-y-auto lg:overflow-visible">
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center space-y-4">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
              <p className="text-neutral-700">{t("auth.checkingSession")}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show offline message if trying to login while offline
  if (!isOnline) {
  return (
    <div className="space-y-4 max-h-[calc(100dvh-2rem)] overflow-y-auto">
        <Link
          href={homeHref}
          className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />{t("auth.backToHome")}</Link>
        
        <div className="mx-auto w-full max-w-md rounded-2xl border border-neutral-200/80 bg-white p-6 shadow-[0_10px_30px_-14px_rgba(15,23,42,0.12)] sm:p-8">
          <div className="flex flex-col items-center justify-center min-h-[360px] space-y-6 text-center">
            <div className="rounded-2xl bg-yellow-100 p-4">
              <WifiOff className="w-12 h-12 text-yellow-600" />
            </div>
            <div className="space-y-2">
              <h1 className="gs-h1 text-neutral-900">{t("auth.noInternetConnection")}</h1>
              <p className="text-neutral-600 max-w-md">{t("auth.needInternetSignin")}</p>
              <p className="text-sm text-neutral-500 max-w-md">{t("auth.offlineSignedInHint")}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Main content at top on mobile, scrollable if needed */}
      <div className="space-y-3 pb-0 flex-1 min-h-0 overflow-y-auto lg:overflow-visible">
        <Link
          href={homeHref}
          className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900 transition-colors mb-2"
        >
          <ArrowLeft className="h-4 w-4" />{t("auth.backToHome")}</Link>

      {/* No card. A form floating in a bordered, shadowed, hover-glowing box read as a
          widget dropped onto the page; the form IS the page, so it stands directly on the
          ground and the type carries the hierarchy. */}
      <div className="mx-auto w-full max-w-xl py-2 sm:py-6">
      <div className="space-y-6">

      {/* The greeting is the header. No icon chip, no shouted brand eyebrow: the side
          panel and the mobile strip already carry the mark, and repeating it here made
          the form read like a third-party login widget. */}
      <div className="space-y-2">
        <h1 className="gs-headline text-neutral-900">{t("dashboard.welcomeBack")}</h1>
        <p className="text-sm leading-relaxed text-neutral-600 md:text-base">{t("auth.signInToContinue")}</p>
      </div>

      {/* Step line only while a second step is actually on screen. Email + password on a device
          that has signed in before is one step, so promising "Step 1 of 2" there would be a lie.
          The row keeps its height either way so nothing below it jumps. */}
      <div className="min-h-4">
        {stepUp && (
          <p className="text-xs text-neutral-500">
            <span className="font-semibold text-primary">{t("auth.stepOf", { current: 2, total: 2 })}</span>
            <span aria-hidden="true"> · </span>
            {t("auth.emailStep.stepLabel")}
          </p>
        )}
      </div>

      {stepUp ? (
        <EmailCodeStep
          challengeToken={stepUp.challengeToken}
          emailHint={stepUp.emailHint}
          codeSent={stepUp.codeSent}
          resendInSeconds={stepUp.resendInSeconds}
          onVerified={async (session, user) => {
            try {
              const entered = await enterWithSession(session, user);
              if (!entered) setStepUp(null);
            } catch {
              setStepUp(null);
              setStatus("error");
              setMessage(t("auth.emailStep.startAgain"));
            }
          }}
          onRestart={(restartMessage) => {
            setStepUp(null);
            setPassword("");
            setStatus(restartMessage ? "error" : "idle");
            setMessage(restartMessage ?? null);
          }}
        />
      ) : (
        <form 
          data-login-method="password"
          className="space-y-4 pb-1" 
          onSubmit={handlePasswordSubmit}
        >
          <label className="flex flex-col gap-2 text-sm text-neutral-700">{t("auth.email")}<div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-500 pointer-events-none" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                className="w-full rounded-md border border-neutral-200 bg-white pl-11 pr-4 py-3 text-base text-neutral-900 placeholder:text-neutral-400 placeholder:opacity-60 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
                style={{ colorScheme: "light" }}
              />
            </div>
          </label>
          
          <label className="flex flex-col gap-2 text-sm text-neutral-700">{t("auth.password")}<div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-500 pointer-events-none" />
              <input
                type={showPassword ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="w-full rounded-md border border-neutral-200 bg-white pl-11 pr-12 py-3 text-base text-neutral-900 placeholder:text-neutral-400 placeholder:opacity-60 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
                style={{ colorScheme: "light" }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-neutral-500 hover:text-neutral-900 transition-colors rounded focus:outline-none focus:ring-2 focus:ring-primary/20"
                tabIndex={-1}
                aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </label>
          
          <div className="flex items-center justify-end text-sm">
            <Link href="/auth/forgot-password" className="text-primary font-semibold hover:underline">{t("auth.forgotPasswordLink")}</Link>
          </div>
          
          {turnstileStalled && (
            <p className="rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-900" role="status">
              We could not finish the security check. This is usually a connection problem rather
              than anything to do with your account. Tap below to try again.
            </p>
          )}

          <Button
            type={turnstileStalled ? "button" : "submit"}
            onClick={turnstileStalled ? retryTurnstile : undefined}
            fullWidth
            loading={
              status === "loading" ||
              (turnstilePending && !turnstileStalled && !isAwaitingInteraction)
            }
            disabled={turnstileStalled ? false : !isFormValid()}
          >
            {status === "loading"
              ? t("auth.signingIn")
              : turnstileStalled
              ? "Try the security check again"
              : isAwaitingInteraction
              ? "Sign in"
              : turnstilePending
              ? "Completing verification…"
              : "Sign in"}
          </Button>
        </form>
      )}


      {/* Status message */}
      {message && (
        <div ref={messageRef} className="text-sm space-y-2">
          <div className={status === "error" ? "text-red-700" : "text-primary"}>{message}</div>
          {/* Show "Forgot password" link when login fails */}
          {status === "error" && email && (
            <div className="flex flex-wrap items-center gap-3 text-red-700">
              <Link
                href="/auth/forgot-password"
                className="text-sm hover:underline font-medium"
              >{t("auth.forgotPasswordLink")}</Link>
            </div>
          )}
        </div>
      )}

        {/* The security check sits directly under whichever form is on screen, because all three
            steps (phone, email, OTP) share this single widget. It used to render below the
            "Sign up" line at the very bottom of the card, so when Cloudflare asked someone to tick
            "Verify you are human" the box was nowhere near what they were looking at, and the page
            told them the check had failed instead of telling them to tick it. */}
        {isAwaitingInteraction && !turnstileStalled && (
          <p className="text-sm text-neutral-600" role="status">
            Please tick the box below to confirm you are human, then continue.
          </p>
        )}
        <div ref={turnstileAreaRef}>
          <TurnstileComponent />
        </div>

        {/* Don't-have-an-account link — keep visible so users who land on /login
            by mistake can still reach signup. */}
        <div className="pt-2">
          <p className="text-sm leading-relaxed text-neutral-600 text-center">
            {t("auth.dontHaveAccount")}{" "}
            <Link href="/auth/signup" className="text-primary font-semibold hover:underline">{t("landing.signUp")}</Link>
          </p>
        </div>

      </div>
      </div>
      </div>

      {/* Terms and Privacy Policy - aligned with copyright at bottom (desktop) and sticky at bottom (mobile) */}
      <div
        className="pt-8 flex-shrink-0 mt-auto px-2 sm:px-3"
        style={{
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
          paddingLeft: "calc(0.5rem + env(safe-area-inset-left, 0px))",
          paddingRight: "calc(0.5rem + env(safe-area-inset-right, 0px))",
        }}
      >
        <p className="text-xs text-neutral-500 text-center">
          {t("auth.bySigningInAgree")}{" "}
          <Link href="/terms" className="text-primary hover:underline">{t("settings.termsOfService")}</Link>
          {", "}
          <Link href="/privacy" className="text-primary hover:underline">{t("settings.privacyPolicy")}</Link>
          {t("auth.andSeparator")}
          <Link href="/cookies" className="text-primary hover:underline">{t("settings.cookiePolicy")}</Link>.
        </p>
      </div>
    </div>
  );
}

export default function LoginPage() {
  const { t } = useTranslations();
  return (
    <Suspense fallback={
      <div className="flex min-h-[400px] items-center justify-center bg-white">
        <div className="text-center space-y-4">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="text-neutral-700">{t("common.loading")}</p>
        </div>
      </div>
    }>
      <LoginPageContent />
    </Suspense>
  );
}
